import React from "react";

const cleanText = (txt) => {
  if (!txt) return "";
  return String(txt)
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

/**
 * Format duration from milliseconds to h:mm:ss
 */
export const fmtDuration = (ms) => {
  if (!ms) return "—";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * Format datetime from timestamp or array to YYYY-MM-DD h:mm AM/PM
 */
export const fmtDateTime = (ts) => {
  if (!ts) return "—";
  let d;
  if (Array.isArray(ts)) {
    if (ts.length < 3) return "—";
    const [year, month, day, hour = 0, minute = 0, second = 0] = ts;
    d = new Date(year, month - 1, day, hour, minute, second);
  } else {
    d = new Date(ts);
  }
  if (isNaN(d.getTime())) return "—";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${yyyy}-${mm}-${dd} ${hours}:${minutes} ${ampm}`;
};

/**
 * Summarize attempts for an exam
 */
export const summarizeExamResults = (attempts, totalQs) => {
  const completedAttempts = attempts.filter(a => a.score !== null && a.score !== undefined);

  const avgScore = completedAttempts.length
    ? Math.round(completedAttempts.reduce((s, a) => s + a.score, 0) / completedAttempts.length)
    : 0;
  const minScore = completedAttempts.length ? Math.min(...completedAttempts.map(a => a.score)) : 0;
  const maxScore = completedAttempts.length ? Math.max(...completedAttempts.map(a => a.score)) : 0;

  const durations = completedAttempts.filter(a => a.durationMs).map(a => a.durationMs);
  const avgDurationMs = durations.length
    ? durations.reduce((s, d) => s + d, 0) / durations.length
    : null;
  const minDurationMs = durations.length ? Math.min(...durations) : null;
  const maxDurationMs = durations.length ? Math.max(...durations) : null;

  // Score histogram buckets: 0-20, 21-40, 41-60, 61-80, 81-100
  const buckets = [0, 0, 0, 0];
  completedAttempts.forEach(a => {
    const score = a.score || 0;
    let b = 0;
    if (score <= 20) b = 0;
    else if (score <= 40) b = 1;
    else if (score < 60) b = 2;
    else b = 3;
    buckets[b]++;
  });
  const maxBucket = Math.max(...buckets, 1);

  // Per-question correctness %
  const qCorrectPct = Array.from({ length: totalQs }, (_, qi) => {
    const withResult = completedAttempts.filter(a => Array.isArray(a.questionResults) && a.questionResults.length > qi);
    if (!withResult.length) return null;
    const correct = withResult.filter(a => a.questionResults[qi]).length;
    return Math.round((correct / withResult.length) * 100);
  });

  return {
    completedAttempts,
    avgScore,
    minScore,
    maxScore,
    avgDurationMs,
    minDurationMs,
    maxDurationMs,
    buckets,
    maxBucket,
    qCorrectPct
  };
};

/**
 * Safely returns questions list using fallback hierarchy.
 */
export const getDisplayQuestions = (resultsQuestions, attempts, test) => {
  if (Array.isArray(resultsQuestions) && resultsQuestions.length > 0) {
    return resultsQuestions;
  }
  if (test && Array.isArray(test.questions) && test.questions.length > 0) {
    return test.questions;
  }
  return [];
};

/**
 * Computes individual question correctness percentage across all students.
 */
export const getQuestionCorrectPct = (q, attempts) => {
  const completedAttempts = attempts.filter(a => a.score !== null && a.score !== undefined);
  if (completedAttempts.length === 0) return null;

  const qId = String(q.id ?? q.questionId ?? q.question_id ?? q.qid ?? q.examQuestionId ?? "");
  if (!qId) return null;

  let correctCount = 0;
  let count = 0;

  completedAttempts.forEach(att => {
    if (Array.isArray(att.questionDetails) && att.questionDetails.length > 0) {
      const qTextClean = cleanText(q.question);
      const detail = att.questionDetails.find(d => cleanText(d.questionText) === qTextClean);
      if (detail) {
        count++;
        const userOption = detail.userOption ? detail.userOption.toUpperCase() : null;
        const correctOpt = (q.correctOption || "").toUpperCase();
        if (userOption && correctOpt && userOption === correctOpt) {
          correctCount++;
        }
      }
    }
  });

  if (count === 0) return null;
  return Math.round((correctCount / count) * 100);
};

/**
 * Renders custom check/cross/NA/dash UI inside a clickable container.
 */
export const renderQuestionCell = (att, q, test, onCellClick) => {
  const hasAttempt = att.score !== null && att.score !== undefined;
  if (!hasAttempt) {
    return <span style={{ color: "#94a3b8" }}>—</span>;
  }

  const qId = String(q.id ?? q.questionId ?? q.question_id ?? q.qid ?? q.examQuestionId ?? "");

  let isCorrect = null;
  let isNotAnswered = true;
  let userOption = null;

  // 1. Try finding in questionDetails
  if (Array.isArray(att.questionDetails) && att.questionDetails.length > 0) {
    const qTextClean = cleanText(q.question);
    const detail = att.questionDetails.find(d => cleanText(d.questionText) === qTextClean);
    if (detail) {
      userOption = detail.userOption ? detail.userOption.toUpperCase() : null;
      isNotAnswered = !userOption || userOption === "";
      const correctOpt = (q.correctOption || "").toUpperCase();
      isCorrect = !!(userOption && correctOpt && userOption === correctOpt);
    }
  }

  // 2. Fallback to local storage attempt index mapping
  if (isCorrect === null && Array.isArray(test?.questions)) {
    const qIndex = test.questions.findIndex(tq => {
      const tqId = String(tq.id ?? tq.questionId ?? tq.question_id ?? tq.qid ?? tq.examQuestionId ?? "");
      return tqId === qId;
    });
    if (qIndex !== -1 && Array.isArray(att.questionResults) && att.questionResults.length > qIndex) {
      isCorrect = att.questionResults[qIndex];
      userOption = att.selectedOptions?.[qIndex];
      isNotAnswered = !userOption || userOption === "";
    }
  }

  const cellStyle = {
    cursor: "pointer",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: "bold",
    userSelect: "none",
    padding: "6px"
  };

  if (isNotAnswered) {
    return (
      <div style={cellStyle} onClick={() => onCellClick(att, q, "NA", userOption, isCorrect)}>
        <span style={{ color: "#64748b" }}>∅</span>
      </div>
    );
  }

  if (isCorrect) {
    return (
      <div style={cellStyle} onClick={() => onCellClick(att, q, "correct", userOption, isCorrect)}>
        <span style={{ color: "#22c55e" }}>✔️</span>
      </div>
    );
  }

  return (
    <div style={cellStyle} onClick={() => onCellClick(att, q, "incorrect", userOption, isCorrect)}>
      <span style={{ color: "#ef4444" }}>❌</span>
    </div>
  );
};
